'use client'

import { cn } from '@brew/ui/cn'
import {
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from '@brew/ui/sheet'
import { useTranslations } from 'next-intl'
import type { ComponentProps, ReactNode } from 'react'
import { BrewSectionLabel } from './brew-form'
import { type LucideIcon, XIcon } from './icons'

/**
 * Right-side sheets in the dialog kit's language: `BrewSheetContent` (the
 * skinned, floating panel) › `BrewSheetHeader` › `BrewSheetBody` filled
 * with the kit's `BrewStats` / `BrewSummary` / `BrewList` / `BrewBanner`
 * blocks › optional `BrewSheetFooter`.
 *
 * From `sm` up the panel floats: an inset, fully rounded card instead of a
 * flush edge.
 */
export const BREW_SHEET_SURFACE_CLASS =
  'gap-0 border-0 bg-[var(--dlg-surface)] p-0 text-[var(--dlg-ink)] shadow-[0_0_0_1px_rgb(48_47_44/0.06),-20px_0_60px_-24px_rgb(90_40_0/0.3)] sm:inset-y-2 sm:right-2 sm:h-auto sm:rounded-[22px]'

/** Horizontal inset every skinned sheet body shares with its header. */
export const BREW_SHEET_INSET_CLASS = 'px-7'

const SHEET_WIDTH_CLASS = {
  md: 'sm:max-w-[560px]',
  lg: 'sm:max-w-xl',
  xl: 'sm:max-w-[760px]',
} as const

/**
 * The skinned right-side panel. `loading` dims the content under a spinner
 * line while a refresh lands; the body scrolls inside `BrewSheetBody`.
 */
export function BrewSheetContent({
  width = 'lg',
  loading = false,
  className,
  children,
  ...props
}: ComponentProps<typeof SheetContent> & {
  width?: keyof typeof SHEET_WIDTH_CLASS
  loading?: boolean
}) {
  return (
    <SheetContent
      data-dialog-skin=""
      className={cn(
        BREW_SHEET_SURFACE_CLASS,
        'flex w-full flex-col overflow-hidden',
        SHEET_WIDTH_CLASS[width],
        className
      )}
      {...props}
    >
      {children}
      {loading ? (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-20 bg-[var(--dlg-surface)]/60 backdrop-blur-[1px]"
        />
      ) : null}
    </SheetContent>
  )
}

/** The scrolling body under a `BrewSheetHeader`. */
export function BrewSheetBody({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="brew-sheet-body"
      className={cn(
        'flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto pb-7',
        BREW_SHEET_INSET_CLASS,
        className
      )}
      {...props}
    />
  )
}

/** Pinned actions at the bottom of a sheet; `start` for a caption. */
export function BrewSheetFooter({
  start,
  className,
  children,
}: {
  start?: ReactNode
  className?: string
  children: ReactNode
}) {
  return (
    <div
      data-slot="brew-sheet-footer"
      className={cn(
        'flex shrink-0 items-center gap-3 border-[var(--dlg-divider)] border-t py-4',
        start ? 'justify-between' : 'justify-end',
        BREW_SHEET_INSET_CLASS,
        className
      )}
    >
      {start ? (
        <div className="min-w-0 text-[var(--dlg-muted)] text-dialog-meta">
          {start}
        </div>
      ) : null}
      <div className="flex shrink-0 items-center gap-2">{children}</div>
    </div>
  )
}

const CLOSE_CLASS =
  'flex size-8 shrink-0 items-center justify-center rounded-full text-[var(--dlg-muted)] transition-colors hover:bg-[var(--dlg-tile)] hover:text-[var(--dlg-ink)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--dlg-accent)]'

/**
 * Kicker › title › meta row, with trailing actions and the round close
 * control top-right. The title is the Radix `SheetTitle` (the sheet's
 * accessible name); `description` renders as `SheetDescription`.
 */
export function BrewSheetHeader({
  kicker,
  title,
  titleHint,
  description,
  meta,
  actions,
  onClose,
  sticky = false,
  className,
}: {
  kicker?: ReactNode
  title: ReactNode
  /** Native tooltip for a title that may truncate (a long email, a file name). */
  titleHint?: string
  description?: ReactNode
  /** Under the title: a status `BrewBadge`, a timestamp, a recipient. */
  meta?: ReactNode
  /** Left of the close control: a "View" link or a quiet button. */
  actions?: ReactNode
  /**
   * Close through the sheet's own handler. Without it the control is a
   * Radix `SheetClose`, which routes through the root's `onOpenChange`.
   */
  onClose?: () => void
  /** Pin the header while the body scrolls under it. */
  sticky?: boolean
  className?: string
}) {
  const tCommon = useTranslations('common')
  const closeIcon = (
    <>
      <XIcon className="size-4" strokeWidth={2} aria-hidden="true" />
      <span className="sr-only">{tCommon('actions.close')}</span>
    </>
  )
  return (
    <div
      data-slot="brew-sheet-header"
      className={cn(
        'flex shrink-0 items-start gap-3 pt-6 pr-4 pb-5 pl-7',
        sticky &&
          'sticky top-0 z-10 bg-[var(--dlg-surface)]/95 backdrop-blur-md',
        className
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        {kicker ? (
          <div className="font-semibold text-[var(--dlg-accent-deep)] text-caption uppercase leading-[14px] tracking-dialog-kicker">
            {kicker}
          </div>
        ) : null}
        <SheetTitle
          title={titleHint}
          className='min-w-0 truncate font-semibold text-[var(--dlg-ink)] text-dialog-title leading-7 tracking-[-0.02em] [font-family:system-ui,-apple-system,"Segoe_UI",Roboto,sans-serif]'
        >
          {title}
        </SheetTitle>
        {description ? (
          <SheetDescription className="text-pretty text-[var(--dlg-body)] text-sm leading-[22px]">
            {description}
          </SheetDescription>
        ) : null}
        {meta ? (
          <div className="flex min-w-0 flex-wrap items-center gap-2 text-[var(--dlg-muted)] text-dialog-meta">
            {meta}
          </div>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        {actions}
        {onClose ? (
          <button type="button" onClick={onClose} className={CLOSE_CLASS}>
            {closeIcon}
          </button>
        ) : (
          <SheetClose className={CLOSE_CLASS}>{closeIcon}</SheetClose>
        )}
      </div>
    </div>
  )
}

export type BrewSheetTileTone =
  | 'neutral'
  | 'brand'
  | 'success'
  | 'warning'
  | 'danger'

const TILE_TONE: Record<BrewSheetTileTone, string> = {
  neutral: 'bg-[var(--dlg-tile)] text-[var(--dlg-body)]',
  brand: 'bg-[var(--dlg-accent-wash)] text-[var(--dlg-accent-deep)]',
  success: 'bg-[var(--dlg-success-wash)] text-[var(--dlg-success-ink)]',
  warning: 'bg-[var(--dlg-warning-wash)] text-[var(--dlg-warning-ink)]',
  danger: 'bg-[var(--dlg-danger-wash)] text-[var(--dlg-danger-ink)]',
}

/** The 28px tinted icon tile that leads a `BrewListRow` in a sheet. */
export function BrewSheetIconTile({
  icon: Icon,
  tone = 'neutral',
  className,
}: {
  icon: LucideIcon
  tone?: BrewSheetTileTone
  className?: string
}) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'flex size-7 shrink-0 items-center justify-center rounded-lg',
        TILE_TONE[tone],
        className
      )}
    >
      <Icon className="size-3.5" strokeWidth={2} />
    </span>
  )
}

/** A body section's label: the kit's `BrewSectionLabel`. */
export const BrewSheetSectionLabel = BrewSectionLabel
